import React, { useCallback, useEffect, useRef } from 'react'
import {
	Box,
	Checkbox,
	Collapse,
	FormControlLabel,
	Grid,
	Paper,
	Switch,
	TextField,
	Typography,
} from '@material-ui/core'
import CheckIcon from '@material-ui/icons/Check'
import ImageOutlinedIcon from '@material-ui/icons/ImageOutlined'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import LocationOnIcon from '@material-ui/icons/LocationOn'
import moment from 'moment'
import DraggableImageBase from '../../../DraggableImage'
import {
	CampaignPreviewCard,
	CampaignReviewTopRow,
	NumSpinner,
} from '../components'
import { SUITABLE_ORDER_TYPES } from '../../../../constants/dataConfig'
import {
	CampaignConfirmationState,
	CampaignDefinition,
	CampaignImageConfig,
} from '../CampaignModalSkeleton.types'
import { Menu, Section } from '../../MarketingCampaigns.promo.services'
import {
	CAMPAIGN_ICON_TEXT_GAP_PX,
	G,
	GL,
	useCampaignModalSkeletonStyles,
} from '../CampaignModalSkeleton.styles'
import { CAMPAIGN_IMAGE_MAX_BYTES } from '../CampaignModalSkeleton.imageUrl'
import { CampaignType } from 'src/constants/marketingCampaign'

const DraggableImage = DraggableImageBase as unknown as React.FC<any>

export interface ConfirmationStepProps<TRule> {
	definition: CampaignDefinition<TRule>
	rule: TRule
	confirmation: CampaignConfirmationState
	setConfirmation: React.Dispatch<React.SetStateAction<CampaignConfirmationState>>
	availableOrderTypes: string[]
	selectedShopIds: string[]
	shops: Record<string, any>
	includeRestaurantsStep: boolean
	sections?: Section[]
	allMenus?: Menu
	campaignImageConfig: CampaignImageConfig | null
	setCampaignImageConfig: React.Dispatch<React.SetStateAction<CampaignImageConfig | null>>
}

function getCampaignSummaryText(type: string, rule: any): string {
	switch (type) {
		case CampaignType.BUY_N_GET_M_CHEAPEST_AT_DISCOUNT: {
			const total = (rule.numProductsPaid || 0) + (rule.numProductsDiscounted || 0)
			return `${total} achetés, ${rule.numProductsDiscounted || 0} à -${rule.discountPercent || 0}%`
		}
		case CampaignType.BUY_ONE_GET_ONE_PRODUCT:
			return '1 acheté = 1 offert sur le même produit'
		case CampaignType.BUY_ONE_GET_ONE_DIFFERENT_PRODUCT:
			return '1 acheté = 1 cadeau offert'
		case CampaignType.GLOBAL_PRODUCT_DISCOUNT_AFTER_THRESHOLD:
			return `Réduction globale dès ${rule.threshold || 0}€`
		case CampaignType.PRODUCT_DISCOUNT:
			return 'Produits en réduction'
		case CampaignType.PRODUCT_DISCOUNT_AFTER_THRESHOLD:
			return `Remise produit dès ${rule.threshold || 0}€`
		case CampaignType.FREE_DELIVERY_AFTER_THRESHOLD:
			return `Frais de livraison offerts dès ${rule.threshold || 0}€`
		case CampaignType.FIXED_AMOUNT_DISCOUNT_AFTER_THRESHOLD:
			return `Code promo de ${rule.discountValue || 0}€ dès ${rule.threshold || 0}€`
		default:
			return ''
	}
}

export function ConfirmationStep<TRule>({
	definition,
	rule,
	confirmation,
	setConfirmation,
	availableOrderTypes,
	selectedShopIds,
	shops,
	includeRestaurantsStep,
	sections = [],
	allMenus,
	campaignImageConfig: _campaignImageConfig,
	setCampaignImageConfig,
}: ConfirmationStepProps<TRule>) {
	const classes = useCampaignModalSkeletonStyles()
	const Summary = definition.ConfirmationSummary
	// Captures isPermanent at mount; correct only because the parent mounts this step lazily — if that changes, rekey on modal session.
	const initialIsPermanentRef = useRef(confirmation.isPermanent)

	useEffect(() => {
		if (definition.forceDeliveryOrderType) {
			setConfirmation((c) => ({ ...c, orderTypes: ['delivery'] }))
		}
	}, [definition.forceDeliveryOrderType, setConfirmation])

	const handleInputChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>, field: string) => {
			const value = e?.target?.value
			if (value === undefined) {
				return
			}
			setConfirmation((prev) => ({ ...prev, [field]: value }))
		},
		[setConfirmation],
	)

	const handleDateChange = useCallback(
		(date: Date, field: 'startDate' | 'endDate') => {
			setConfirmation((prev) => ({ ...prev, [field]: date }))
		},
		[setConfirmation],
	)

	const handleOrderTypeToggle = useCallback(
		(orderType: string) => {
			setConfirmation((prev) => {
				const currentOrderTypes = prev.orderTypes || []
				const isSelected = currentOrderTypes.includes(orderType)
				if (isSelected) {
					if (currentOrderTypes.length === 1) {
						return prev
					}
					return {
						...prev,
						orderTypes: currentOrderTypes.filter((t) => t !== orderType),
					}
				}
				return {
					...prev,
					orderTypes: [...currentOrderTypes, orderType],
				}
			})
		},
		[setConfirmation],
	)

	const handleLimitToggle = useCallback(
		(_e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
			setConfirmation((prev) => ({
				...prev,
				hasLimit: checked,
				limit: checked ? prev.limit || 1 : null,
			}))
		},
		[setConfirmation],
	)

	const handlePermanentToggle = useCallback(
		(_e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
			setConfirmation((prev) => ({ ...prev, isPermanent: checked }))
		},
		[setConfirmation],
	)

	function SectionCard({
		title,
		children,
		className,
		marginBottom = 8,
	}: {
		title: string
		children: React.ReactNode
		className?: string
		marginBottom?: number
	}) {
		return (
			<Paper
				variant="outlined"
				className={className}
				style={{ borderRadius: 10, marginBottom, overflow: 'hidden' }}
			>
				<Box style={{ padding: '11px 16px', background: '#fafafa', borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
					<Typography variant="body2" style={{ fontWeight: 500, color: '#424242' }}>
						{title}
					</Typography>
				</Box>
				<Box style={{ padding: '12px 16px' }}>{children}</Box>
			</Paper>
		)
	}

	const showLimit =
		definition.confirmationLimitPolicy === 'required' ||
		definition.confirmationLimitPolicy === 'optional'

	const limitActive =
		definition.confirmationLimitPolicy === 'required' || confirmation.hasLimit

	const summaryContext = {
		sections,
		allMenus,
		selectedShopIds,
	}

	const periodCard = (
		<Paper variant="outlined" style={{ borderRadius: 10, marginBottom: 8, overflow: 'hidden' }}>
			<Box
				display="flex"
				alignItems="center"
				justifyContent="space-between"
				style={{
					padding: '13px 16px',
					background: '#fafafa',
					borderBottom: '1px solid rgba(0,0,0,0.08)',
				}}
			>
				<Box>
					<Typography variant="body2" style={{ fontWeight: 500, color: '#424242' }}>
						Période de l&apos;offre
					</Typography>
					<Typography
						variant="caption"
						style={{
							color: '#757575',
							display: 'block',
							marginTop: 1,
							lineHeight: 1.4,
						}}
					>
						Début et fin (fuseau horaire du compte)
					</Typography>
				</Box>
			</Box>
			<Box style={{ padding: '14px 16px' }}>
				<Box
					display="flex"
					flexDirection={{ xs: 'column', sm: 'row' }}
					style={{ gap: 16 }}
				>
					<TextField
						fullWidth
						variant="outlined"
						size="small"
						label="Date de début"
						type="date"
						value={moment(confirmation.startDate).format('YYYY-MM-DD')}
						onChange={(e) => {
							const v = e.target.value
							if (!v) {
								return
							}
							const d = moment(v, 'YYYY-MM-DD', true)
							if (d.isValid()) {
								handleDateChange(d.toDate(), 'startDate')
							}
						}}
						InputLabelProps={{ shrink: true }}
						inputProps={{ min: moment().format('YYYY-MM-DD') }}
						color="primary"
						InputProps={{ style: { borderRadius: 10 } }}
					/>
					<TextField
						fullWidth
						variant="outlined"
						size="small"
						label="Heure de début"
						type="time"
						value={confirmation.startTime}
						onChange={(e) =>
							handleInputChange(e as React.ChangeEvent<HTMLInputElement>, 'startTime')
						}
						InputLabelProps={{ shrink: true }}
						color="primary"
						InputProps={{ style: { borderRadius: 10 } }}
					/>
				</Box>

				{definition.allowsPermanent && (
					<Box
						display="flex"
						alignItems="center"
						justifyContent="space-between"
						style={{ marginTop: 16 }}
					>
						<Box>
							<Typography variant="body2" style={{ fontWeight: 500 }}>
								Offre permanente
							</Typography>
							<Typography
								variant="caption"
								style={{ color: '#757575', display: 'block', lineHeight: 1.4 }}
							>
								L&apos;offre sera active jusqu&apos;à annulation manuelle
							</Typography>
						</Box>
						<Switch
							checked={confirmation.isPermanent}
							onChange={handlePermanentToggle}
							color="primary"
							inputProps={{ 'aria-label': 'Offre permanente' }}
						/>
					</Box>
				)}

				{!confirmation.isPermanent && (
					<Box
						display="flex"
						flexDirection={{ xs: 'column', sm: 'row' }}
						style={{ gap: 16, marginTop: 16 }}
					>
						<TextField
							fullWidth
							variant="outlined"
							size="small"
							label="Date de fin"
							type="date"
							value={moment(confirmation.endDate).format('YYYY-MM-DD')}
							onChange={(e) => {
								const v = e.target.value
								if (!v) {
									return
								}
								const d = moment(v, 'YYYY-MM-DD', true)
								if (d.isValid()) {
									handleDateChange(d.toDate(), 'endDate')
								}
							}}
							InputLabelProps={{ shrink: true }}
							inputProps={{
								min: moment(confirmation.startDate).format('YYYY-MM-DD'),
							}}
							color="primary"
							InputProps={{ style: { borderRadius: 10 } }}
						/>
						<TextField
							fullWidth
							variant="outlined"
							size="small"
							label="Heure de fin"
							type="time"
							value={confirmation.endTime}
							onChange={(e) =>
								handleInputChange(e as React.ChangeEvent<HTMLInputElement>, 'endTime')
							}
							InputLabelProps={{ shrink: true }}
							color="primary"
							InputProps={{ style: { borderRadius: 10 } }}
						/>
					</Box>
				)}

				{confirmation.isPermanent && !initialIsPermanentRef.current && (
					<Box
						style={{
							padding: '8px 12px',
							background: '#fff8e1',
							border: '1px solid #ffe082',
							borderRadius: 8,
							marginTop: 14,
						}}
					>
						<Typography variant="caption" style={{ color: '#8d6e00' }}>
							Cela supprimera la date de fin programmée. La campagne sera active
							jusqu&apos;à annulation.
						</Typography>
					</Box>
				)}
			</Box>
		</Paper>
	)

	const orderTypesCard = definition.showOrderTypesInConfirmation && (
		<Paper variant="outlined" style={{ borderRadius: 10, marginBottom: 8, overflow: 'hidden' }}>
			<Box style={{ padding: '11px 16px', background: '#fafafa', borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
				<Typography variant="body2" style={{ fontWeight: 500, color: '#424242' }}>
					Modes de vente
				</Typography>
			</Box>
			<Box style={{ padding: '12px 16px' }}>
				<Grid container spacing={1}>
					{availableOrderTypes.map((orderType) => (
						<Grid item xs={12} sm={6} md={4} key={orderType}>
							<FormControlLabel
								control={
									<Checkbox
										checked={confirmation.orderTypes?.includes(orderType)}
										onChange={() => handleOrderTypeToggle(orderType)}
										color="primary"
										style={{ padding: 0 }}
									/>
								}
								label={
									<Typography
										variant="body2"
										component="span"
										style={{ color: '#424242' }}
									>
										{SUITABLE_ORDER_TYPES[orderType] || orderType}
									</Typography>
								}
								style={{
									marginLeft: 0,
									marginRight: 0,
									whiteSpace: 'nowrap',
									alignItems: 'center',
									gap: CAMPAIGN_ICON_TEXT_GAP_PX,
								}}
							/>
						</Grid>
					))}
				</Grid>
			</Box>
		</Paper>
	)

	const limitCard = showLimit && (
		<Paper variant="outlined" style={{ borderRadius: 10, marginBottom: 16, overflow: 'hidden' }}>
			{definition.confirmationLimitPolicy === 'optional' ? (
				<Box
					display="flex"
					alignItems="center"
					justifyContent="space-between"
					style={{
						padding: '13px 16px',
						borderBottom: confirmation.hasLimit ? '1px solid rgba(0,0,0,0.08)' : 'none',
						background: confirmation.hasLimit ? 'rgba(0, 153, 115, 0.03)' : '#fff',
					}}
				>
					<Box>
						<Typography variant="body2" style={{ fontWeight: 500 }}>
							Limite d&apos;utilisation par panier
						</Typography>
						<Typography
							variant="caption"
							style={{ color: '#757575', display: 'block', marginTop: 1, lineHeight: 1.4 }}
						>
							Nombre maximal d&apos;offres par commande
						</Typography>
					</Box>
					<Switch
						checked={confirmation.hasLimit}
						onChange={handleLimitToggle}
						color="primary"
						inputProps={{ 'aria-label': 'Limite par panier' }}
					/>
				</Box>
			) : (
				<Box style={{ padding: '13px 16px', background: '#fafafa', borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
					<Typography variant="body2" style={{ fontWeight: 500 }}>
						Limite d&apos;utilisation par panier
					</Typography>
					<Typography
						variant="caption"
						style={{ color: '#757575', display: 'block', marginTop: 1, lineHeight: 1.4 }}
					>
						Obligatoire pour ce type de campagne
					</Typography>
				</Box>
			)}
			<Collapse in={limitActive}>
				<Box display="flex" alignItems="center" style={{ padding: '13px 16px' }}>
					<Box flex={1}>
						<Typography variant="body2" style={{ color: '#424242' }}>
							Nombre maximal d&apos;utilisations
						</Typography>
					</Box>
					<NumSpinner
						value={confirmation.limit}
						onChange={(v) =>
							setConfirmation((p) => ({
								...p,
								limit: v,
							}))
						}
						min={1}
						max={99}
						placeholder="1"
					/>
				</Box>
			</Collapse>
		</Paper>
	)

	const previewImageSlot = confirmation.image ? (
			<DraggableImage
				multiple={false}
				maxFileSize={CAMPAIGN_IMAGE_MAX_BYTES}
			aspect={1.25}
			imgExtension={['.png', '.jpeg', '.jpg']}
			picture={[{ imageUrl: confirmation.image }]}
			defaultText="Image (500Ko max)"
			saveConfigOnly
			restrictPosition
			cropContainerClassName={classes.previewImageArea}
			cropProps={{
				width: '100%',
				maxWidth: '100%',
				boxSizing: 'border-box',
			}}
			cropStyle={classes.previewImageArea}
			setCroppedImage={() => {}}
			setImageConfig={(config: CampaignImageConfig) => {
				setCampaignImageConfig(config)
				const isLocalPreview =
					config.imageUrl.startsWith('data:') ||
					config.imageUrl.startsWith('blob:')
				if (isLocalPreview) {
					setConfirmation((c) =>
						c.image === config.imageUrl ? c : { ...c, image: config.imageUrl },
					)
				}
			}}
			hideRemoveButton
		/>
	) : (
		<ImageOutlinedIcon style={{ fontSize: 36, color: '#b0bec5' }} />
	)

	return (
		<Box>
			<Box
				display="flex"
				alignItems="center"
				gridGap={CAMPAIGN_ICON_TEXT_GAP_PX}
				style={{
					padding: '10px 14px',
					background: '#f1fdf7',
					border: `1px solid ${G}`,
					borderRadius: 10,
					marginBottom: 14,
				}}
			>
				<CheckIcon style={{ color: G, fontSize: 22 }} />
				<Box>
					<Typography variant="body2" style={{ fontWeight: 500, color: G }}>
						Vérifiez le récapitulatif et les options
					</Typography>
					<Typography
						variant="caption"
						style={{ color: '#757575', display: 'block', lineHeight: 1.4 }}
					>
						Dates, modes de vente et limites éventuelles
					</Typography>
				</Box>
			</Box>

			{definition.supportsCampaignNaming ? (
				<CampaignReviewTopRow
					left={
						<CampaignPreviewCard
							imageSlot={previewImageSlot}
							title={confirmation.name || 'Sans titre'}
							summary={getCampaignSummaryText(definition.type, rule)}
						/>
					}
					right={
						<>
							{selectedShopIds.length > 0 && (
								<SectionCard
									title={`Établissements concernés (${selectedShopIds.length})`}
									className={classes.reviewSectionCard}
									marginBottom={0}
								>
									<Box display="flex" flexWrap="wrap" gridGap={5}>
										{selectedShopIds.map((id) => (
											<Box key={id} component="span" style={{ margin: 2 }}>
												<ChipShop name={shops[id]?.name || id} />
											</Box>
										))}
									</Box>
								</SectionCard>
							)}

							<SectionCard
								title="Règle promotionnelle"
								className={classes.reviewSectionCard}
								marginBottom={0}
							>
								<Summary rule={rule} {...summaryContext} />
							</SectionCard>
						</>
					}
				/>
			) : (
				<>
					{includeRestaurantsStep && selectedShopIds.length > 0 && (
						<SectionCard title="Restaurants ciblés">
							<Box display="flex" flexWrap="wrap" gridGap={5}>
								{selectedShopIds.map((id) => (
									<Box key={id} component="span" style={{ margin: 2 }}>
										<ChipShop name={shops[id]?.name || id} />
									</Box>
								))}
							</Box>
						</SectionCard>
					)}
					<SectionCard title="Règle promotionnelle">
						<Summary rule={rule} {...summaryContext} />
					</SectionCard>
				</>
			)}

			<Box
				display="flex"
				flexDirection="column"
				style={{ gap: 16, marginTop: 8 }}
			>
				{periodCard}
				{orderTypesCard}
				{definition.forceDeliveryOrderType && (
					<Box
						style={{
							padding: '10px 14px',
							background: GL,
							border: `1px solid ${G}`,
							borderRadius: 10,
						}}
					>
						<Typography variant="body2" style={{ color: G }}>
							Cette offre s&apos;applique uniquement aux commandes en{' '}
							<strong>livraison</strong>.
						</Typography>
					</Box>
				)}
				{limitCard}
			</Box>

			{selectedShopIds.length > 1 && (
				<Box
					display="flex"
					gridGap={CAMPAIGN_ICON_TEXT_GAP_PX}
					alignItems="flex-start"
					style={{
						padding: '10px 14px',
						background: GL,
						border: `1px solid ${G}`,
						borderRadius: 10,
						marginTop: 16,
					}}
				>
					<InfoOutlinedIcon style={{ color: G, fontSize: 18, marginTop: 2, flexShrink: 0 }} />
					<Typography variant="body2" style={{ color: G }}>
						La campagne sera active sur tous les restaurants sélectionnés dès le lancement,
						sous réserve des dates et des règles ci-dessus.
					</Typography>
				</Box>
			)}
		</Box>
	)
}

function ChipShop({ name }: { name: string }) {
	return (
		<Box
			component="span"
			style={{
				display: 'inline-flex',
				alignItems: 'center',
				gap: CAMPAIGN_ICON_TEXT_GAP_PX,
				padding: '4px 10px',
				border: `1px solid ${G}`,
				borderRadius: 16,
				fontSize: 12,
				color: G,
			}}
		>
			<LocationOnIcon style={{ fontSize: 14 }} />
			{name}
		</Box>
	)
}
